/**
 * Identidad estable del navegador para las push.
 *
 * El `deviceId` identifica este navegador de forma persistente, independiente
 * de la cuenta logueada. Se guarda en `localStorage` (acceso síncrono desde la
 * app) y se espeja en IndexedDB para que el Service Worker —que no puede leer
 * localStorage— lo recupere al manejar `pushsubscriptionchange`.
 *
 * El nombre de la BD/almacén/clave debe coincidir con el del Service Worker
 * (public/sw.js).
 */

const LS_KEY = 'kw-device-id';
export const IDB_NAME = 'knooowy-push';
export const IDB_STORE = 'meta';
export const IDB_DEVICE_KEY = 'deviceId';

function mirrorToIndexedDb(deviceId: string): void {
  try {
    if (typeof indexedDB === 'undefined') return;
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => {
      const db = req.result;
      try {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).put(deviceId, IDB_DEVICE_KEY);
        tx.oncomplete = () => db.close();
      } catch {
        db.close();
      }
    };
  } catch {
    // ignore (modo privado, etc.)
  }
}

/**
 * Devuelve el `deviceId` de este navegador, creándolo la primera vez. Siempre
 * lo re-espeja en IndexedDB para mantener al Service Worker sincronizado.
 */
export function getDeviceId(): string {
  let id: string | null = null;
  try {
    id = localStorage.getItem(LS_KEY);
  } catch {
    // ignore
  }

  if (!id) {
    id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      localStorage.setItem(LS_KEY, id);
    } catch {
      // ignore
    }
  }

  mirrorToIndexedDb(id);
  return id;
}
